import React, {
  useState,
  useEffect,
  useCallback,
  useContext,
} from "react";
import axios from "axios";
import moment from "moment";

import DataGridTable from "../Components/DataGridTable";
import Spinner from "../Components/Spinner";
import Alert from "../Components/Alert";
import { BASE_URL_API } from "../Constants";
import { useAuth } from "../hooks/AuthProvider";
import { AuditLogDetails } from "./AuditLogDetails";

import { LanguageContext } from "../context/LanguageContext";
import { normalizeAuditLogListRow } from "../utils/listRowIds";
import auditTranslations from "../languages/audit.json";

export const AuditLogs = () => {
  const { permissions } = useAuth();
  const { lang } = useContext(LanguageContext);
  const t = auditTranslations[lang]?.auditLogs || auditTranslations.en.auditLogs;

  const canView = permissions["view_audit_logs"];

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState([
    { field: "created_at", sort: "desc" },
  ]);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const [alertState, setAlertState] = useState({
    open: false,
    message: "",
    type: "info",
    position: ["ar", "ur"].includes(lang) ? "left" : "right",
  });

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const showAlert = useCallback(
    (type, message) => {
      setAlertState({
        open: true,
        type,
        message,
        position: ["ar", "ur"].includes(lang) ? "left" : "right",
      });
    },
    [lang]
  );

  const pagingoptions = [10, 20, 50, 100];
  const hasActiveFilters = (filterModel?.items || []).some((item) => {
    if (!item) return false;
    const operator = String(item.operator || "").toLowerCase();
    if (operator === "isempty" || operator === "isnotempty") return true;
    return item.value !== undefined && item.value !== null && String(item.value).trim() !== "";
  });
  const shouldShowGrid = rowCount > 0 || rows.length > 0 || hasActiveFilters;

  const columns = [
    {
      field: "user_name",
      headerName: t.columns.userName,
      width: 180,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "user_email",
      headerName: t.columns.userEmail,
      width: 240,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "action",
      headerName: t.columns.action,
      width: 140,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "type",
      headerName: t.columns.type,
      width: 220,
      align: "left",
      headerAlign: "left",
    },
    {
      field: "status",
      headerName: t.columns.status,
      width: 110,
      align: "center",
      headerAlign: "center",
      valueFormatter: (params) => {
        const value = String(params || "").trim().toLowerCase();
        return t.statuses[value] || params || "";
      },
    },
    {
      field: "created_at",
      headerName: t.columns.createdAt,
      width: 220,
      align: "center",
      headerAlign: "center",
      type: "dateTime",
      valueGetter: (params) => (params ? new Date(params) : null),
      valueFormatter: (params) =>
        params ? moment(params).format("DD/MM/YYYY HH:mm:ss") : "",
    },
  ];

  const fetchAuditLogs = useCallback(async () => {
    if (!canView) {
      setRows([]);
      setRowCount(0);
      return;
    }

    setLoading(true);
    try {
      const activeSort =
        sortModel && sortModel.length > 0 ? sortModel[0] : null;
      const sortField = activeSort?.field || "created_at";
      const sortOrder = activeSort?.sort || "desc";

      const res = await axios.get(`${BASE_URL_API}/audit-logs`, {
        params: {
          page,
          pageSize,
          sortField,
          sortOrder,
          filterModel: JSON.stringify(filterModel || { items: [] }),
        },
      });

      if (res.data && res.data.status === "OK") {
        const { data, pagination } = res.data;
        setRows(Array.isArray(data) ? data.map(normalizeAuditLogListRow) : []);
        setRowCount(pagination?.total ?? data.length);
      } else {
        throw new Error(t.invalidResponse);
      }
    } catch (err) {
      console.error("Fetch audit logs error:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        t.failedLoad;
      showAlert("error", message);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  }, [canView, filterModel, page, pageSize, showAlert, sortModel, t.failedLoad, t.invalidResponse]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const handleRowClick = (params) => {
    setSelectedLog(params.row);
    setDetailsOpen(true);
  };

  if (!canView) {
    return (
      <div
        style={{
          padding: "24px",
          color: "var(--cell-color)",
        }}
      >
        {t.permissionDenied}
      </div>
    );
  }

  return (
    <div
      className="configuration devices-page"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Alert
        open={alertState.open}
        close={() =>
          setAlertState((prev) => ({ ...prev, open: false }))
        }
        message={alertState.message}
        type={alertState.type}
        position={alertState.position}
      />

      <div
        className="device-main"

      >
        <div className="devices-header">
          <div className="devices-header-left">
            <h2
              className="devices-title"
            >
              {t.title}
            </h2>
          </div>
        </div>

        {loading && !shouldShowGrid ? (
          <Spinner />
        ) : shouldShowGrid ? (
          <div
            className="devices-grid-wrapper"
            style={{
              flex: 1,
              minHeight: 0,
              marginTop: "20px",
            }}
          >
            <DataGridTable
              columns={columns}
              rows={rows}
              idcolumn={"audit_log_id"}
              pagingoptions={pagingoptions}
              sortField="created_at"
              sortType="desc"
              handleRowClick={handleRowClick}
              checkboxSelection={false}
              rowSelectionModel={[]}
              setRowSelectionModel={() => {}}
              selectActions={[]}
              showToolbar={true}
              tableKey="audit_logs"
              loading={loading}
              pagination={{
                serverSide: true,
                page,
                pageSize,
                count: rowCount,
                sortModel,
                onPageChange: (newPage) => setPage(newPage),
                onPageSizeChange: (newSize) => {
                  setPageSize(newSize);
                  setPage(1);
                },
                onSortModelChange: (newModel) => {
                  setSortModel(
                    newModel && newModel.length ? newModel : []
                  );
                  setPage(1);
                },
                onFilterModelChange: (newModel) => {
                  setFilterModel(newModel || { items: [] });
                  setPage(1);
                },
              }}
            />
          </div>
        ) : (
          <div
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize:
                "calc(var(--body-font-size) * var(--font-size-multiplier))",
            }}
          >
            {t.empty}
          </div>
        )}
      </div>

      <AuditLogDetails
        open={detailsOpen}
        log={selectedLog}
        onClose={() => setDetailsOpen(false)}
      />
    </div>
  );
};

export default AuditLogs;
